<template>
    <div id="app">
        <!--<ComponentWithPropertiesInstance :component="root" />-->
        <ModalStackComponent ref="modalStack" :root="root" />
        <ToastBox />
    </div>
</template>

<script lang="ts" setup>
import type { Decoder } from '@common/encoding';
import { isSimpleError, isSimpleErrors } from '@common/errors';
import type { PushOptions } from '@browser/navigation';
import { ComponentWithProperties, HistoryManager, ModalStackComponent, NavigationController, useManualPresent } from '@browser/navigation';
import { AsyncComponent } from '@stamhoofd/components/containers/AsyncComponent.ts';

import LoadingView from '@stamhoofd/components/containers/LoadingView.vue';
import PromiseView from '@stamhoofd/components/containers/PromiseView.vue';
import { ErrorBox } from '@stamhoofd/components/errors/ErrorBox.ts';
import { CenteredMessage } from '@stamhoofd/components/overlays/CenteredMessage.ts';
import CenteredMessageView from '@stamhoofd/components/overlays/CenteredMessageView.vue';
import { ModalStackEventBus } from '@stamhoofd/components/overlays/ModalStackEventBus.ts';
import { Toast } from '@stamhoofd/components/overlays/Toast.ts';
import ToastBox from '@stamhoofd/components/overlays/ToastBox.vue';
import { I18nController } from '@stamhoofd/frontend-i18n/I18nController';
import { loadSessionLocale } from '@stamhoofd/frontend-application/SessionLocalization';
import { LocalizedDomains } from '@stamhoofd/frontend-i18n/LocalizedDomains';
import { loadPlatform } from '@stamhoofd/frontend-session/loadPlatform';
import { NetworkManager } from '@stamhoofd/networking/NetworkManager';
import { SessionContext } from '@stamhoofd/frontend-session/SessionContext';
import { SessionManager } from '@stamhoofd/frontend-application/SessionManager';
import { UrlHelper } from '@stamhoofd/networking/UrlHelper';
import { GetWebshopFromDomainResult } from '@stamhoofd/structures';
import { Language } from '@stamhoofd/types/Language';
import { GoogleTranslateHelper, isReservedWebshopPathSegment } from '@stamhoofd/utility';
import { ref, watch } from 'vue';
import { getWebshopRootView } from './getRootView';
import { getWebshopPreviewLanguage, webshopPreviewParameter } from '@stamhoofd/components/webshops/webshopPreview.ts';

const owner = {};

// Shown in an iframe of the dashboard
// The initial url: the url of the page is changed while loading
const preview = window.parent !== window && UrlHelper.initial.getSearchParams().has(webshopPreviewParameter);
const root = new ComponentWithProperties(PromiseView, {
    promise: async () => {
        // get organization
        try {
            // Check if we are on a global domain, and ignore /shops prefixes if needed
            const hostname = window.location.hostname;

            // Ignore this fixed prefix in our next lookup
            UrlHelper.fixedPrefix = null;

            const path = UrlHelper.shared.getParts();
            const usedUri = path[0] && !isReservedWebshopPathSegment(path[0]) ? path[0] : '';
            const response = await NetworkManager.server.request({
                method: 'GET',
                path: '/webshop-from-domain',
                query: {
                    domain: hostname,
                    uri: usedUri,
                },
                decoder: GetWebshopFromDomainResult as Decoder<GetWebshopFromDomainResult>,
            });

            // Yay, we have a webshop! Now mark the full suffix of this webshop as the fixed prefix, so we can just forget about it
            if (usedUri) {
                if (UrlHelper.fixedPrefix) {
                    UrlHelper.fixedPrefix = UrlHelper.fixedPrefix + '/' + usedUri;
                } else {
                    UrlHelper.fixedPrefix = usedUri;
                }
                console.info('Using fixed prefix', UrlHelper.fixedPrefix);
            }
            const isPrerender = navigator.userAgent.toLowerCase().indexOf('prerender') !== -1;

            // Do we need to redirect?
            if (response.data.webshop) {
                try {
                    const url = new URL('https://' + response.data.webshop.getUrl(response.data.organization));

                    if (window.location.hostname.toLowerCase() !== url.hostname.toLowerCase()) {
                        // Redirect
                        const prefix = url.pathname.replace(/^\/+|\/+$/g, '');
                        // Remove starting and trailing slash
                        const u = UrlHelper.initial.getFullHref({ host: url.hostname, removePrefix: true, appendPrefix: prefix });

                        if (isPrerender) {
                            return AsyncComponent(() => import('./views/errors/PrerenderRedirectView.vue'), { location: u });
                        }

                        window.location.href = u;
                        return new ComponentWithProperties(LoadingView, {});
                    }
                } catch (e) {
                    console.error(e);
                }
            }

            // A preview uses the language of the dashboard, which might not be saved yet
            const savedLanguage = response.data.webshop?.meta.defaultLanguage ?? null;
            const defaultLanguage = preview ? getWebshopPreviewLanguage(UrlHelper.initial.getSearchParams(), savedLanguage) : savedLanguage;
            const webshopLanguage = defaultLanguage ?? response.data.organization.language ?? Language.Dutch;

            I18nController.skipUrlPrefixForLocale = webshopLanguage + '-' + response.data.organization.address.country;

            // The platform has to exist before the session does, so resolve it first (from cache,
            // or over the network). This is the seam that becomes a tenant/domain lookup later.
            const { platform, fromCache } = await loadPlatform();

            // Set session
            const session = new SessionContext(response.data.organization, platform);
            await session.loadFromStorage();

            await loadSessionLocale(session, {
                defaultCountry: response.data.organization.address.country,
                defaultLanguage: webshopLanguage,
                country: response.data.organization.address.country,
                locales: (defaultLanguage ?? response.data.organization.language)
                    ? {
                            // For now always force the webshop's default language
                            // if we add translations to webshops, we should add all the setup languages here
                            [response.data.organization.address.country]: [webshopLanguage],
                        }
                    : undefined, // Multi lingual webshop
            });

            await session.checkSSO();
            await SessionManager.prepareSessionForUsage(session);

            if (session.requiresPlatformPrivateConfig() && !session.platform.privateConfig) {
                // The bootstrapped platform is the public one, but this user needs the private
                // config to reliably calculate its permissions: upgrade it with an authenticated
                // request.
                await session.fetchPlatform();
            } else if (fromCache) {
                // We served a platform that came from storage, so refresh it in the background.
                session.fetchPlatform().catch(console.error);
            }

            if (!response.data.webshop) {
                return new ComponentWithProperties(NavigationController, {
                    root: AsyncComponent(() => import('./views/ChooseWebshopView.vue'), {
                        organization: response.data.organization,
                        webshops: response.data.webshops,
                    }),
                });
            }

            const organization = response.data.organization;
            const webshop = response.data.webshop;

            document.title = webshop.meta.name + ' - ' + organization.name;

            return await getWebshopRootView(session, webshop, { preview });
        } catch (e) {
            console.log(e);
            // Check if we have an organization on this domain
            if (!I18nController.shared) {
                try {
                    await loadSessionLocale(null, {
                        defaultLanguage: Language.Dutch,
                    });
                } catch (e) {
                    console.error(e);
                }
            }

            if (isSimpleError(e) || isSimpleErrors(e)) {
                if (!(e.hasCode('invalid_domain') || e.hasCode('unknown_organization') || e.hasCode('unknown_webshop'))) {
                    Toast.fromError(e).show();

                    return AsyncComponent(() => import('./views/errors/InvalidWebshopView.vue'), {
                        errorBox: new ErrorBox(e),
                    });
                }
                // Redirect

                const marketingWebshops = 'https://' + LocalizedDomains.marketing + '/webshops';

                const isPrerender = navigator.userAgent.toLowerCase().indexOf('prerender') !== -1;

                if (isPrerender) {
                    return AsyncComponent(() => import('./views/errors/PrerenderRedirectView.vue'), { location: marketingWebshops });
                }

                // window.location.href = marketingWebshops
            }
            return AsyncComponent(() => import('./views/errors/InvalidWebshopView.vue'), {});
        }
    },
});

const modalStack = ref<InstanceType<typeof ModalStackComponent> | null>(null);

function created() {
    if (GoogleTranslateHelper.isGoogleTranslateDomain(window.location.hostname)) {
        // Enable translations
        document.documentElement.translate = true;
    }

    if (STAMHOOFD.environment === 'development') {
        // ComponentWithProperties.debug = true
    }
    // An iframe shares the browser history with the dashboard: going back in either one could navigate the other
    if (!preview) {
        HistoryManager.activate();
    } else {
        // Scrolling past the top or bottom continues in the dashboard around the preview
        document.documentElement.classList.add('webshop-preview');
    }
}

created();
const manualPresent = useManualPresent();

watch(modalStack, (modalStack: InstanceType<typeof ModalStackComponent> | null) => {
    if (modalStack === null) {
        return;
    }

    ModalStackEventBus.addListener(owner, 'present', async (options: PushOptions | ComponentWithProperties) => {
        if (options instanceof ComponentWithProperties) {
            await modalStack.present({ components: [options] });
        } else {
            await modalStack.present(options);
        }
    });

    CenteredMessage.addListener(owner, async (centeredMessage) => {
        await manualPresent(modalStack.present, {
            components: [
                new ComponentWithProperties(CenteredMessageView, {
                    centeredMessage,
                }, {
                    forceCanHaveFocus: true,
                }),
            ],
            modalDisplayStyle: 'overlay',
        });
    });
});
</script>

<style lang="scss">
// We need to include the component styling of vue-app-navigation first
@use "@stamhoofd/scss/main";
@use "@browser/navigation/dist/main.css" as VueAppNavigation;
@use "@stamhoofd/scss/base/dark-modus";

body {
    --st-sheet-width: 450px;
}

html.webshop-preview {
    &, body, .st-view > main {
        overscroll-behavior: auto;
    }
}
</style>
